<template>
  <modal :name="modalName" width="94%" height="auto" :max-width="900" :max-height="820" :adaptive="true" :scrollable="true"
    :click-to-close="false" @before-close="onBeforeClose">
    <section class="communication-template-editor" role="dialog" aria-modal="true" aria-labelledby="communication-template-editor-title">
      <AppModalHeader design="new" eyebrow="Communication · Templates" :title="isNew ? 'Create template' : 'Edit template'"
        title-id="communication-template-editor-title" @close="close" />
      <div class="communication-template-editor__body">
        <div class="communication-template-editor__fields">
          <AppFormField design="new" label="Name" label-for="communication-template-name" required>
            <AppInput id="communication-template-name" v-model="form.name" design="new" maxlength="160" placeholder="Template name" />
          </AppFormField>
          <AppFormField design="new" label="Suggested for" label-for="communication-template-kind">
            <AppSelect :value="form.contact_type || null" design="new" input-id="communication-template-kind" aria-label="Suggested for"
              :options="contactOptions" :reduce="option => option.value" label="label" :clearable="false"
              append-to-body :calculate-position="positionTemplateDropdown" @input="form.contact_type = $event || ''" />
          </AppFormField>
        </div>
        <label class="communication-template-editor__active"><input v-model="form.active" type="checkbox"> Active — available to the team for copying</label>
        <div class="communication-template-editor__language-heading">
          <h3>Message by language</h3>
          <div class="communication-template-editor__add-language">
            <AppSelect :value="newLanguage" design="new" aria-label="Language to add" :options="addableLanguages"
              :reduce="language => language.code" label="name" placeholder="Add language…" :clearable="false"
              append-to-body :calculate-position="positionTemplateDropdown" @input="newLanguage = $event" />
            <AppButton design="new" theme="dark" size="small" :disabled="!newLanguage" @click.native="addLanguage">Add</AppButton>
          </div>
        </div>
        <p v-if="languageError" class="communication-template-editor__error" role="alert">{{ languageError }}</p>
        <div class="communication-template-editor__language-tabs" role="tablist" aria-label="Template languages">
          <button v-for="text in form.texts" :key="text.language_code" type="button" role="tab"
            :aria-selected="String(selectedLanguage === text.language_code)" :class="{ 'is-active': selectedLanguage === text.language_code }"
            @click="selectedLanguage = text.language_code">{{ languageName(text.language_code) }}</button>
        </div>
        <AppFormField v-if="selectedText" design="new" :label="`${languageName(selectedLanguage)} message`" label-for="communication-template-body" required>
          <AppTextarea id="communication-template-body" :value="selectedText.body" design="new"
            :placeholder="'Write the message and insert variables as {{1}}, {{2}}…'" @input="changeBody" />
        </AppFormField>
        <p class="communication-template-editor__hint">Use numbered placeholders: <code v-pre>{{1}}</code>, <code v-pre>{{2}}</code>. Every language uses the same variables.</p>
        <div class="communication-template-editor__variables-heading">
          <h3>Variables</h3>
          <button type="button" @click="addVariable">Add variable</button>
        </div>
        <p class="communication-template-editor__hint">Add a variable here or type its numbered placeholder in the message. Every language must use the same placeholders.</p>
        <div v-if="form.variables.length" class="communication-template-editor__variable-table">
          <div v-for="variable in form.variables" :key="variable.key" class="communication-template-editor__variable-row">
            <code>{{ placeholderLabel(variable.key) }}</code>
            <AppInput v-model="variable.description" design="new" :aria-label="`What variable ${variable.key} means`" maxlength="300" placeholder="What to enter" />
            <AppInput v-model="variable.example" design="new" :aria-label="`Example for variable ${variable.key}`" maxlength="300" placeholder="Example (optional)" />
            <button type="button" :aria-label="`Remove variable ${variable.key}`" @click="removeVariable(variable.key)"><i class="pi pi-trash" aria-hidden="true" /></button>
          </div>
        </div>
        <p v-if="!canSave && form.name && form.texts.some(text => text.body)" class="communication-template-editor__hint">Complete every language and describe every variable before saving.</p>
        <p v-if="error" class="communication-template-editor__error" role="alert">{{ error }}</p>
      </div>
      <footer class="communication-template-editor__footer">
        <AppButton design="new" theme="dark" :disabled="saving" @click.native="close">Cancel</AppButton>
        <AppButton design="new" theme="success" :disabled="saving || !canSave" @click.native="save">{{ saving ? 'Saving…' : 'Save template' }}</AppButton>
      </footer>
    </section>
  </modal>
</template>

<script>
import { getLanguages, postCommunicationTemplate, putCommunicationTemplate } from '@/http'
import { errorMessage } from './model'
import { positionTemplateDropdown } from './templateSelectDropdown'

const MAIN_LANGUAGES = ['us', 'ru', 'de', 'fr', 'es']
const LANGUAGE_NAMES = { us: 'English', ru: 'Russian', de: 'German', fr: 'French', es: 'Spanish' }
const CONTACT_OPTIONS = [
  { value: null, label: 'Any contact' },
  { value: 'welcome', label: 'Welcome' },
  { value: 'starts_tomorrow', label: 'Safari starts tomorrow' },
  { value: 'evening_follow_up', label: 'Evening follow-up' }
]
const PLACEHOLDER = /{{([1-9][0-9]*)}}/g

function keys(body) {
  return [...new Set([...String(body || '').matchAll(PLACEHOLDER)].map(match => match[1]))].sort((a, b) => Number(a) - Number(b))
}

export default {
  name: 'CommunicationTemplateEditor',
  props: { template: { type: Object, required: true } },
  data() {
    const isNew = Boolean(this.template.isNew)
    const texts = isNew ? [{ language_code: 'us', body: '' }] : this.template.texts.map(text => ({ language_code: text.language_code, body: text.body }))
    return {
      modalName: 'communication-template-editor',
      isNew,
      form: {
        code: isNew ? `custom-${Date.now()}` : this.template.code,
        name: isNew ? '' : this.template.name,
        contact_type: isNew ? '' : this.template.contact_type,
        active: isNew ? true : this.template.active,
        variables: isNew ? [] : this.template.variables.map(variable => ({ ...variable })),
        texts
      },
      selectedLanguage: texts[0].language_code,
      newLanguage: null,
      contactOptions: CONTACT_OPTIONS,
      languages: [],
      languageError: '',
      saving: false,
      error: ''
    }
  },
  computed: {
    selectedText() { return this.form.texts.find(text => text.language_code === this.selectedLanguage) },
    addableLanguages() { return this.languages.filter(language => !this.form.texts.some(text => text.language_code === language.code)) },
    canSave() {
      if (!this.form.name.trim() || !this.form.texts.length || this.form.texts.some(text => !text.body.trim())) return false
      const reference = keys(this.form.texts[0].body)
      const expected = reference.map((_, index) => String(index + 1))
      if (reference.join(',') !== expected.join(',') || this.form.variables.length !== reference.length) return false
      if (this.form.variables.some(variable => !variable.description.trim() || !reference.includes(variable.key))) return false
      return this.form.texts.every(text => {
        const remaining = text.body.replace(PLACEHOLDER, '')
        return !remaining.includes('{{') && !remaining.includes('}}') && keys(text.body).join(',') === reference.join(',')
      })
    }
  },
  mounted() { this.$modal.show(this.modalName); this.loadLanguages() },
  methods: {
    positionTemplateDropdown,
    placeholderLabel(key) { return `{{${key}}}` },
    languageName(code) { return LANGUAGE_NAMES[code] || this.languages.find(language => language.code === code)?.name || code.toUpperCase() },
    async loadLanguages() {
      try {
        const { data } = await getLanguages()
        this.languages = data.filter(language => MAIN_LANGUAGES.includes(language.code) && language.active !== false)
      } catch (error) {
        this.languageError = 'Could not load available languages. Refresh the page to try again.'
      }
    },
    addLanguage() {
      if (!this.newLanguage || this.form.texts.some(text => text.language_code === this.newLanguage)) return
      this.form.texts.push({ language_code: this.newLanguage, body: '' })
      this.selectedLanguage = this.newLanguage
      this.newLanguage = null
    },
    changeBody(value) { this.selectedText.body = value; this.syncVariables() },
    syncVariables() {
      const allKeys = [...new Set(this.form.texts.flatMap(text => keys(text.body)))].sort((a, b) => Number(a) - Number(b))
      for (const key of allKeys) {
        if (!this.form.variables.some(variable => variable.key === key)) this.form.variables.push({ key, description: '', example: '' })
      }
      this.form.variables.sort((a, b) => Number(a.key) - Number(b.key))
    },
    addVariable() {
      const key = String(Math.max(0, ...this.form.variables.map(variable => Number(variable.key))) + 1)
      this.form.variables.push({ key, description: '', example: '' })
    },
    removeVariable(key) { this.form.variables = this.form.variables.filter(variable => variable.key !== key) },
    close() { if (!this.saving) this.$modal.hide(this.modalName) },
    onBeforeClose() { this.$emit('close') },
    async save() {
      if (this.saving || !this.canSave) return
      this.saving = true
      this.error = ''
      try {
        const request = this.isNew ? postCommunicationTemplate(this.form) : putCommunicationTemplate(this.template.id, this.form)
        await request
        this.$emit('saved')
        this.$modal.hide(this.modalName)
      } catch (error) {
        this.error = errorMessage(error, 'Could not save this template.')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.communication-template-editor { color: $ui-text; background: $ui-surface; font-family: $ui-font-body; }
.communication-template-editor__body { display: grid; gap: $ui-space-16; padding: $ui-space-24; }
.communication-template-editor__fields { display: grid; grid-template-columns: 1fr 1fr; gap: $ui-space-16; }
.communication-template-editor__active { display: flex; align-items: center; gap: $ui-space-8; }
.communication-template-editor__active input { display: inline-block; width: 16px; height: 16px; margin: 0; appearance: auto; accent-color: $ui-green; }
.communication-template-editor__language-heading { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: $ui-space-12; }
.communication-template-editor h3 { margin: 0; color: $ui-ink; font-size: $ui-font-size-section-title; }
.communication-template-editor__add-language { display: flex; gap: $ui-space-8; }
.communication-template-editor__add-language .select { width: 200px; }
.communication-template-editor__language-tabs { display: flex; flex-wrap: wrap; gap: $ui-space-8; }
.communication-template-editor__language-tabs button { padding: $ui-space-8 $ui-space-12; border: 1px solid $ui-line; border-radius: $ui-tab-radius; background: $ui-surface; color: $ui-text; cursor: pointer; }
.communication-template-editor__language-tabs button.is-active { border-color: $ui-blue; background: $ui-blue-soft; color: $ui-blue; }
.communication-template-editor__body ::v-deep textarea { min-height: 220px; resize: vertical; white-space: pre-wrap; }
.communication-template-editor__hint { margin: 0; color: $ui-muted; font-size: $ui-font-size-metadata; }
.communication-template-editor__variable-table { display: grid; gap: $ui-space-8; }
.communication-template-editor__variables-heading { display: flex; align-items: center; justify-content: space-between; gap: $ui-space-8; }
.communication-template-editor__variables-heading button, .communication-template-editor__variable-row button { border: 0; background: none; color: $ui-blue; cursor: pointer; }
.communication-template-editor__variable-row { display: grid; grid-template-columns: 70px 1fr 1fr 32px; align-items: center; gap: $ui-space-8; }
.communication-template-editor__variable-row code { color: $ui-blue; }
.communication-template-editor__error { margin: 0; color: $ui-red; }
.communication-template-editor__footer { display: flex; justify-content: flex-end; gap: $ui-space-12; padding: $ui-space-16 $ui-space-24; border-top: 1px solid $ui-line; }
@media (max-width: 700px) { .communication-template-editor__fields, .communication-template-editor__variable-row { grid-template-columns: 1fr; } }
</style>
